import { ref, unref, toRaw } from 'vue'
import { toArray } from './use-tree'

export const PINNED_MENUS_MAX = 20
export const PINNED_GROUP_KEY = '__pinned_group__'

function buildDuplicateLabelKeys(menus) {
  const labelToKeys = new Map()
  const walk = (items) => {
    for (const item of toArray(items)) {
      const menu = toRaw(item)
      const label = typeof menu?.label === 'string' ? menu.label.trim() : ''
      const nodeKey = menu?.key ?? menu?.id
      if (label && nodeKey) {
        if (!labelToKeys.has(label)) labelToKeys.set(label, [])
        labelToKeys.get(label).push(nodeKey)
      }
      if (toArray(menu?.items).length) walk(menu.items)
    }
  }
  walk(toArray(toRaw(menus)))
  const keys = new Set()
  for (const ks of labelToKeys.values()) {
    if (ks.length >= 2) ks.forEach((k) => keys.add(k))
  }
  return keys
}

export function usePinnedMenus({
  menus,
  findMenu,
  findMenuContext,
  pinnedMenuIds,
  onPinnedMenuIdsUpdate
}) {
  const pinnedMenus = ref([])

  // Keyboard "grabbed" state: original (un-prefixed) id of the pinned menu currently
  // picked up via keyboard. Only one can be grabbed at a time. `null` = nothing grabbed.
  const grabbedKey = ref(null)

  const getOriginalId = (id) => (id?.startsWith('pinned-') ? id.slice('pinned-'.length) : id)
  const getPinnedId = (id) => `pinned-${getOriginalId(id)}`
  const getMenuOriginalKey = (menuItem) => {
    const raw = menuItem?.key ?? menuItem?.id
    if (raw == null) return null
    const str = String(raw)
    return str.startsWith('pinned-') ? str.slice('pinned-'.length) : str
  }

  const getIds = () => {
    const raw = pinnedMenuIds?.value
    return Array.isArray(raw) ? raw : []
  }

  const buildPinnedItem = (menu, duplicateLabelKeys) => {
    // eslint-disable-next-line no-unused-vars
    const { badge: _badge, ...menuWithoutBadge } = menu
    const pinnedItem = {
      ...menuWithoutBadge,
      id: getPinnedId(menu.id),
      key: getPinnedId(menu.key)
    }
    const context = findMenuContext?.(menu.key ?? menu.id)
    if (context?.parent) {
      if (context.parent.icon) pinnedItem.parentIcon = context.parent.icon
    }
    const menuKey = menu.key ?? menu.id
    const dupKeys = duplicateLabelKeys ?? buildDuplicateLabelKeys(unref(menus))
    if (menuKey && dupKeys.has(menuKey) && context?.parent?.label) {
      pinnedItem.pinnedLabelWithParent = context.parent.label
    }
    return pinnedItem
  }

  const applyFromOriginalIds = (originalIds) => {
    if (!originalIds?.length) {
      pinnedMenus.value = []
      return
    }
    const duplicateLabelKeys = buildDuplicateLabelKeys(unref(menus))
    pinnedMenus.value = originalIds
      .map((id) => {
        const menu = findMenu(id)
        return menu ? buildPinnedItem(menu, duplicateLabelKeys) : null
      })
      .filter(Boolean)
  }

  const syncPinnedMenus = () => applyFromOriginalIds(getIds())
  const emitPinnedUpdate = (nextIds) => onPinnedMenuIdsUpdate?.([...nextIds])

  const handleItemPin = ({ menuItem } = {}) => {
    const originalId = getOriginalId(menuItem?.id ?? menuItem?.key)
    const current = [...getIds()]
    const idx = current.indexOf(originalId)
    const isPinned = idx >= 0
    if (!isPinned && current.length >= PINNED_MENUS_MAX) return
    if (isPinned) {
      current.splice(idx, 1)
    } else {
      current.push(originalId)
    }
    emitPinnedUpdate(current)
    applyFromOriginalIds(current)
  }

  const reorderPinnedMenus = (fromIndex, toIndex) => {
    const newMenus = [...pinnedMenus.value]
    const [moved] = newMenus.splice(fromIndex, 1)
    newMenus.splice(toIndex, 0, moved)
    const nextOriginalIds = newMenus.map((m) => getOriginalId(m.id))
    emitPinnedUpdate(nextOriginalIds)
    pinnedMenus.value = newMenus
  }

  const handleSortableReorder = ({ fromIndex, toIndex }) => {
    if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)) return null
    if (fromIndex === toIndex) return null
    if (fromIndex < 0 || toIndex < 0) return null
    if (fromIndex >= pinnedMenus.value.length || toIndex >= pinnedMenus.value.length) return null
    reorderPinnedMenus(fromIndex, toIndex)
    return {
      moved: true,
      newIndex: toIndex,
      total: pinnedMenus.value.length
    }
  }

  const handleKeyboardReorder = ({ menuItem, direction }) => {
    if (!menuItem || !direction) return null
    const index = pinnedMenus.value.findIndex((m) => m.key === menuItem.key)
    if (index === -1) return null
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= pinnedMenus.value.length) return null
    return handleSortableReorder({ fromIndex: index, toIndex: newIndex })
  }

  // ---------- Keyboard grab / release / move state machine ----------

  const isItemGrabbed = (menuItem) => {
    const key = getMenuOriginalKey(menuItem)
    return !!key && grabbedKey.value === key
  }

  const hasGrabbedItem = () => grabbedKey.value != null

  const releasePinnedGrab = () => {
    if (grabbedKey.value != null) grabbedKey.value = null
  }

  const togglePinnedGrab = (menuItem) => {
    const key = getMenuOriginalKey(menuItem)
    if (!key) return null
    if (grabbedKey.value === key) {
      grabbedKey.value = null
      return 'released'
    }
    grabbedKey.value = key
    return 'grabbed'
  }

  /**
   * Process a keydown originating from a pinned drag-handle.
   *
   * Bubbling rules:
   *   - Returning a non-null result means the event was consumed and we
   *     have already called preventDefault + stopPropagation on it.
   *   - Returning null means the caller should let the event bubble (e.g. an
   *     ArrowUp on a non-grabbed handle still goes to outer tree navigation).
   *
   * Result shapes:
   *   { type: 'grab', state: 'grabbed' | 'released' }
   *   { type: 'reorder', moved: boolean, newIndex?, total? }
   *   { type: 'release-on-escape' }
   */
  const handlePinnedHandleKeydown = ({ event, menuItem }) => {
    if (!event || !menuItem) return null
    const code = event.code

    if (code === 'Space' || code === 'Enter') {
      const state = togglePinnedGrab(menuItem)
      if (!state) return null
      event.preventDefault()
      event.stopPropagation()
      return { type: 'grab', state }
    }

    if (code === 'Escape') {
      if (!isItemGrabbed(menuItem)) return null
      releasePinnedGrab()
      event.preventDefault()
      event.stopPropagation()
      return { type: 'release-on-escape' }
    }

    if (code === 'ArrowUp' || code === 'ArrowDown') {
      // Only swallow arrow keys while grabbed; otherwise let the event bubble
      // so outer tree navigation (move focus to prev/next treeitem) still works.
      if (!isItemGrabbed(menuItem)) return null
      const direction = code === 'ArrowUp' ? -1 : 1
      const result = handleKeyboardReorder({ menuItem, direction }) ?? { moved: false }
      event.preventDefault()
      event.stopPropagation()
      return { type: 'reorder', ...result }
    }

    return null
  }

  return {
    pinnedMenus,
    syncPinnedMenus,
    handleItemPin,
    handleSortableReorder,
    handleKeyboardReorder,
    grabbedKey,
    isItemGrabbed,
    hasGrabbedItem,
    releasePinnedGrab,
    togglePinnedGrab,
    handlePinnedHandleKeydown
  }
}
